"use client";

import { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import type { Swiper as SwiperClass } from "swiper";
import "swiper/css";
import "swiper/css/navigation";
import { ChevronLeft, ChevronRight } from "lucide-react";
import Image from "next/image";
import { Heading } from "@/components/ui/Heading";
import { cn } from "@/core/lib/utils";
import Link from "next/link";
import { loadThemeComponent } from "@/core/config/theme-component-loader";
const CustomButton = loadThemeComponent("CustomButton");

const isVideoUrl = (url?: string) => {
    if (!url) return false;
    return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url);
};

interface SliderItem {
    title?: string;
    description?: string;
    buttonContent?: string;
    link?: string;
    image?: { url: string; description?: string };
    image_mobile?: { url: string; description?: string };
}

interface FullSliderWidgetProps {
    data: {
        items?: SliderItem[];
    };
}

function SliderMedia({
    url,
    alt,
    className,
    onEnded,
}: {
    url: string;
    alt?: string;
    className?: string;
    onEnded?: () => void;
}) {
    if (isVideoUrl(url)) {
        return (
            <video
                src={url}
                autoPlay
                muted
                playsInline
                onEnded={onEnded}
                className={cn("absolute inset-0 w-full h-full object-cover z-0", className)}
            />
        );
    }
    return <Image src={url} alt={alt || ""} fill className={className} />;
}

export default function FullSliderWidget({ data }: FullSliderWidgetProps) {
    const items = data?.items || [];
    const swiperRef = useRef<SwiperClass | null>(null);
    const advancingRef = useRef(false);

    if (items.length === 0) return null;

    const isSlideVideoAt = (index: number) => {
        const slide = items[index];
        return isVideoUrl(slide?.image?.url) || isVideoUrl(slide?.image_mobile?.url);
    };

    const syncAutoplayWithActiveSlide = (swiper: SwiperClass) => {
        advancingRef.current = false;
        if (isSlideVideoAt(swiper.realIndex)) {
            swiper.autoplay.stop();
        } else {
            swiper.autoplay.start();
        }
    };

    const handleVideoEnded = () => {
        const swiper = swiperRef.current;
        if (!swiper || advancingRef.current) return;
        advancingRef.current = true;
        swiper.slideNext();
    };

    return (
        <div className="col-span-12 relative">
            <Swiper
                modules={[Navigation, Autoplay]}
                spaceBetween={30}
                slidesPerView={1}
                navigation={{
                    nextEl: ".full-slider-next",
                    prevEl: ".full-slider-prev",
                }}
                loop
                autoplay={{ delay: 3000, disableOnInteraction: false }}
                onSwiper={(swiper) => {
                    swiperRef.current = swiper;
                    syncAutoplayWithActiveSlide(swiper);
                }}
                onSlideChange={syncAutoplayWithActiveSlide}
                className="w-full"
            >
                {items.map((slide, index) => {
                    const Content = (
                        <div className="relative flex justify-center items-center h-[calc(100vh-4.5rem)]">
                            <div className="hidden lg:flex w-full h-full absolute inset-0">
                                {slide.image?.url && (
                                    <SliderMedia
                                        url={slide.image.url}
                                        alt={slide.image.description || ""}
                                        className="w-full h-full object-cover z-0 absolute"
                                        onEnded={handleVideoEnded}
                                    />
                                )}
                            </div>
                            <div className="flex lg:hidden w-full h-full absolute inset-0">
                                {(slide.image_mobile?.url || slide.image?.url) && (
                                    <SliderMedia
                                        url={slide.image_mobile?.url || slide.image?.url || ""}
                                        alt={slide.image_mobile?.description || slide.image?.description || ""}
                                        className="w-full h-full object-cover z-0 absolute"
                                        onEnded={handleVideoEnded}
                                    />
                                )}
                            </div>

                            <div className="absolute inset-0 bg-black/20 z-0" />

                            {(slide.title || slide.description || (slide.buttonContent && slide.link)) && (
                                <div className="text-primary-100 absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 z-10 space-y-3 text-center">
                                    {slide.title && (
                                        <Heading level={1} className="text-primary-0 text-[calc(1.2rem+0.3vw)]">
                                            {slide.title}
                                        </Heading>
                                    )}
                                    {slide.description && (
                                        <Heading level={2} className="text-primary-0">
                                            {slide.description}
                                        </Heading>
                                    )}
                                    {slide.buttonContent && slide.link && (
                                        <CustomButton size="lg" variant="black" asChild>
                                            <Link href={slide.link}>{slide.buttonContent}</Link>
                                        </CustomButton>
                                    )}
                                </div>
                            )}
                        </div>
                    );

                    return (
                        <SwiperSlide key={index}>
                            {slide.link && !slide.buttonContent ? <Link href={slide.link}>{Content}</Link> : Content}
                        </SwiperSlide>
                    );
                })}

                <button className="full-slider-prev absolute ltr:left-4 top-1/2 -translate-y-1/2 z-10 text-primary-100 cursor-pointer disabled:opacity-0 disabled:cursor-default rtl:rotate-180 rtl:right-4">
                    <ChevronLeft className="w-10 h-10 stroke-1 text-primary-0" />
                </button>
                <button className="full-slider-next absolute ltr:right-4 top-1/2 -translate-y-1/2 z-10 text-primary-100 cursor-pointer disabled:opacity-0 disabled:cursor-default rtl:rotate-180 rtl:left-4">
                    <ChevronRight className="w-10 h-10 stroke-1 text-primary-0" />
                </button>
            </Swiper>
        </div>
    );
}